import { supabase } from './supabase.js';

export const personalTodosService = {
  // Get all todos for a user
  async getTodos(ownerId, filters = {}) {
    console.log('📋 Getting personal todos for:', ownerId, 'with filters:', filters);
    
    try {
      let query = supabase
        .from('personal_todos')
        .select('*')
        .eq('owner_id', ownerId);

      // Apply filters
      if (filters.status) {
        if (Array.isArray(filters.status)) {
          query = query.in('status', filters.status);
        } else {
          query = query.eq('status', filters.status);
        }
      }
      if (filters.priority) {
        query = query.eq('priority', filters.priority);
      }
      if (filters.category) {
        query = query.eq('category', filters.category);
      }
      if (filters.isPinned) {
        query = query.eq('is_pinned', filters.isPinned);
      }
      if (filters.dueDate) {
        query = query.eq('due_date', filters.dueDate);
      }
      if (filters.overdue) {
        query = query.lt('due_date', new Date().toISOString().split('T')[0])
                      .neq('status', 'completed');
      }

      // Sort by priority, pinned status, and due date
      query = query
        .order('is_pinned', { ascending: false })
        .order('priority', { ascending: false })
        .order('due_date', { ascending: true, nullsLast: true })
        .order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      // console.log('✅ Personal todos retrieved:', data?.length || 0);
      return data || [];
    } catch (error) {
      // Don't spam console with database schema errors
      if (error?.code === 'PGRST200' && error?.message?.includes('personal_todo_categories')) {
        console.warn('⚠️ Personal todos schema issue - returning empty array');
        return [];
      }
      console.error('❌ Error getting personal todos:', error);
      throw error;
    }
  },

  // Get todos by specific date (convenience method)
  async getTodosByDate(ownerId, dateString) {
    return this.getTodos(ownerId, { dueDate: dateString });
  },

  // Create a new todo
  async createTodo(ownerId, todoData) {
    console.log('➕ Creating personal todo:', { ownerId, todoData });
    
    try {
      const { data, error } = await supabase
        .from('personal_todos')
        .insert({
          owner_id: ownerId,
          title: todoData.title,
          description: todoData.description || null,
          status: todoData.status || 'pending',
          priority: todoData.priority || 'normal',
          due_date: todoData.dueDate || null,
          category: todoData.category || 'general',
          tags: todoData.tags || [],
          is_pinned: todoData.isPinned || false,
          source_type: todoData.sourceType || 'manual',
          source_id: todoData.sourceId || null,
          estimated_minutes: todoData.estimatedMinutes || null,
          notes: todoData.notes || null
        })
        .select('*')
        .single();

      if (error) throw error;

      console.log('✅ Personal todo created:', data?.id);
      return data;
    } catch (error) {
      console.error('❌ Error creating personal todo:', error);
      throw error;
    }
  },

  // Update a todo
  async updateTodo(todoId, ownerId, updates) {
    console.log('✏️ Updating personal todo:', todoId);
    
    try {
      const updateData = {
        updated_at: new Date().toISOString()
      };

      // Map updates to database columns
      if (updates.title !== undefined) updateData.title = updates.title;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.status !== undefined) updateData.status = updates.status;
      if (updates.priority !== undefined) updateData.priority = updates.priority;
      if (updates.dueDate !== undefined) updateData.due_date = updates.dueDate;
      if (updates.category !== undefined) updateData.category = updates.category;
      if (updates.tags !== undefined) updateData.tags = updates.tags;
      if (updates.isPinned !== undefined) updateData.is_pinned = updates.isPinned;
      if (updates.estimatedMinutes !== undefined) updateData.estimated_minutes = updates.estimatedMinutes;
      if (updates.notes !== undefined) updateData.notes = updates.notes;
      
      // Handle completion
      if (updates.status === 'completed') {
        updateData.completed_at = new Date().toISOString();
      } else if (updates.status && updates.status !== 'completed') {
        updateData.completed_at = null;
      }

      const { data, error } = await supabase
        .from('personal_todos')
        .update(updateData)
        .eq('id', todoId)
        .eq('owner_id', ownerId)
        .select('*')
        .single();

      if (error) throw error;
      if (!data) throw new Error('Todo not found or unauthorized');

      console.log('✅ Personal todo updated:', todoId);
      return data;
    } catch (error) {
      console.error('❌ Error updating personal todo:', error);
      throw error;
    }
  },

  // Complete a todo
  async completeTodo(todoId, ownerId) {
    return this.updateTodo(todoId, ownerId, { status: 'completed' });
  },

  // Delete a todo
  async deleteTodo(todoId, ownerId) {
    console.log('🗑️ Deleting personal todo:', todoId);
    
    try {
      const { error } = await supabase
        .from('personal_todos')
        .delete()
        .eq('id', todoId)
        .eq('owner_id', ownerId);

      if (error) throw error;

      console.log('✅ Personal todo deleted:', todoId);
      return { success: true };
    } catch (error) {
      console.error('❌ Error deleting personal todo:', error);
      throw error;
    }
  },

  // Get todo categories for a user
  async getCategories(ownerId) {
    console.log('📂 Getting todo categories for:', ownerId);
    
    try {
      const { data, error } = await supabase
        .from('personal_todo_categories')
        .select('*')
        .eq('owner_id', ownerId)
        .order('name');

      if (error) throw error;

      console.log('✅ Todo categories retrieved:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('❌ Error getting todo categories:', error);
      throw error;
    }
  },

  // Create a new category
  async createCategory(ownerId, categoryData) {
    console.log('➕ Creating todo category:', { ownerId, categoryData });
    
    try {
      const { data, error } = await supabase
        .from('personal_todo_categories')
        .insert({
          owner_id: ownerId,
          name: categoryData.name,
          color: categoryData.color || '#6B7280',
          emoji: categoryData.emoji || null,
          description: categoryData.description || null
        })
        .select()
        .single();

      if (error) throw error;

      console.log('✅ Todo category created:', data?.id);
      return data;
    } catch (error) {
      console.error('❌ Error creating todo category:', error);
      throw error;
    }
  },

  // Get todo statistics
  async getStats(ownerId) {
    console.log('📊 Getting todo stats for:', ownerId);
    
    try {
      const { data, error } = await supabase
        .rpc('get_personal_todo_stats', { p_owner_id: ownerId });

      if (error) throw error;

      const stats = data?.[0] || {
        total_todos: 0,
        pending_todos: 0,
        completed_todos: 0,
        overdue_todos: 0,
        due_today_todos: 0
      };

      console.log('✅ Todo stats retrieved:', stats);
      return stats;
    } catch (error) {
      console.error('❌ Error getting todo stats:', error);
      throw error;
    }
  },

  // Create default categories for a new user
  async createDefaultCategories(ownerId) {
    console.log('🏗️ Creating default categories for:', ownerId);
    
    try {
      const { data, error } = await supabase
        .rpc('create_default_todo_categories', { p_owner_id: ownerId });

      if (error) throw error;

      console.log('✅ Default categories created');
      return { success: true };
    } catch (error) {
      console.error('❌ Error creating default categories:', error);
      throw error;
    }
  }
};